import { cardPreview, LOCAL } from "@mn/shared";
import type * as Y from "yjs";
import { Button } from "@/components/ui";
import { useT } from "@/lib/i18n";
import type { Actions } from "./actions";
import { useBoard } from "./BoardContext";
import { useFlaggedCards, useYField } from "./hooks";
import { useBoardUI } from "./store";

/** Cards flagged `unsorted` are hidden from the canvas and listed here until placed. */
export function UnsortedPanel({ actions, onClose }: { actions: Actions; onClose: () => void }) {
  const { handle, canEdit } = useBoard();
  const t = useT();
  const unsorted = useFlaggedCards(handle.types.cards, "unsorted");
  return (
    <aside className="absolute bottom-0 right-0 top-0 z-30 flex max-md:w-full md:w-80 flex-col border-l border-border bg-panel shadow-panel">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="display text-xl">.{t.unsorted}</span>
        <button type="button" onClick={onClose} className="text-muted hover:text-text">
          ✕
        </button>
      </div>
      <p className="border-b border-border px-4 py-2 text-[12px] text-dim">{t.unsortedHint}</p>
      <ul className="flex-1 overflow-auto">
        {unsorted.length === 0 ? <li className="p-4 text-sm text-dim">—</li> : null}
        {unsorted.map((id) => (
          <Row key={id} id={id} ymap={handle.types.cards.get(id)!} actions={actions} canEdit={canEdit} />
        ))}
      </ul>
    </aside>
  );
}

function Row({
  id,
  ymap,
  actions,
  canEdit,
}: {
  id: string;
  ymap: Y.Map<unknown>;
  actions: Actions;
  canEdit: boolean;
}) {
  const t = useT();
  const { handle } = useBoard();
  const type = useYField<string>(ymap, "type");
  const place = () => {
    const { camera, viewport } = useBoardUI.getState();
    handle.doc.transact(() => {
      ymap.set("unsorted", false);
      ymap.set("x", Math.round(viewport.w / 2 / camera.z - camera.x - 120));
      ymap.set("y", Math.round(viewport.h / 2 / camera.z - camera.y - 40));
    }, LOCAL);
    useBoardUI.getState().select([id]);
  };
  return (
    <li className="flex items-center gap-2 border-b border-border px-4 py-2 text-sm">
      <span className="min-w-0 flex-1">
        <span className="block truncate">{cardPreview(ymap)}</span>
        <span className="text-[11px] text-dim">{t.cardTypes[type] ?? type}</span>
      </span>
      {canEdit ? (
        <>
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={place}>
            {t.place}
          </Button>
          <button
            type="button"
            title={t.moveToTrash}
            onClick={() => actions.delete([id])}
            className="text-dim hover:text-error"
          >
            ✕
          </button>
        </>
      ) : null}
    </li>
  );
}
